"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.invoiceSummaryQuerySchema = exports.listInvoicesQuerySchema = exports.createPaymentSchema = exports.invoicePdfArchiveSchema = exports.INVOICE_PDF_ARCHIVE_MAX = exports.stornoInvoiceSchema = exports.issueInvoiceSchema = exports.updateInvoiceDraftSchema = exports.createInvoiceDraftSchema = exports.invoiceLineInputSchema = exports.PAYMENT_MEANS_CODES = void 0;
const zod_1 = require("zod");
const constants_1 = require("../constants");
const enums_1 = require("../enums");
const common_schema_1 = require("./common.schema");
const VAT_CATEGORY_VALUES = Object.values(enums_1.VatCategory);
const PAYMENT_METHOD_VALUES = Object.values(enums_1.PaymentMethod);
const INVOICE_STATUS_VALUES = Object.values(enums_1.InvoiceStatus);
const PAYMENT_STATUS_VALUES = Object.values(enums_1.PaymentStatus);
/**
 * UN/CEFACT 4461 payment means this app supports: 10 = numerar, 30 = virament
 * bancar, 42 = plată în contul bancar al furnizorului.
 */
exports.PAYMENT_MEANS_CODES = ['10', '30', '42'];
/** Quantity: up to 3 decimals (matches `@db.Decimal(12, 3)`). The sign is
 * allowed by the regex because the same shape describes a stored storno line;
 * DRAFT payloads reject negatives separately (see `quantityField`). */
const QUANTITY_REGEX = /^-?\d+(\.\d{1,3})?$/;
/** Money as entered by a user: non-negative, up to 2 decimals. */
const UNSIGNED_MONEY_REGEX = /^\d+(\.\d{1,2})?$/;
/** Money that may legitimately be negative — a payment against a storno. */
const SIGNED_MONEY_REGEX = /^-?\d+(\.\d{1,2})?$/;
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const ZERO_REGEX = /^-?0+(\.0+)?$/;
/** A syntactically well-formed "YYYY-MM-DD" that is also a real calendar date
 * ("2026-02-30" is not). Dates travel as strings, not `Date`s, because
 * `SeriesService.allocate()` and every chronology comparison work on the
 * calendar date itself — converting to a `Date` first only invites a
 * timezone-shifted off-by-one day. */
function isRealIsoDate(value) {
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}
const isoDateField = zod_1.z
    .string()
    .trim()
    .regex(ISO_DATE_REGEX, 'Data trebuie să fie în formatul YYYY-MM-DD.')
    .refine(isRealIsoDate, 'Dată invalidă.');
/**
 * Quantity on a DRAFT line. Negatives are rejected here on purpose: the only
 * legitimate source of a negative quantity is a storno, which the server
 * generates itself from the original invoice (see the storno endpoint) — a
 * user must never be able to hand-craft a "negative invoice" through the
 * draft API and bypass the credit-note trail.
 */
const quantityField = zod_1.z
    .string()
    .trim()
    .regex(QUANTITY_REGEX, 'Cantitatea trebuie să fie un număr cu cel mult 3 zecimale.')
    .refine((value) => !ZERO_REGEX.test(value), 'Cantitatea nu poate fi zero.')
    .refine((value) => !value.startsWith('-'), 'Cantitatea nu poate fi negativă — folosește stornarea.');
exports.invoiceLineInputSchema = zod_1.z.object({
    productId: zod_1.z.uuid('productId invalid.').optional(),
    description: zod_1.z.string().trim().min(1, 'Descrierea este obligatorie.'),
    quantity: quantityField,
    unitCode: zod_1.z.enum(constants_1.UNIT_CODE_VALUES, 'Unitate de măsură invalidă.'),
    unitLabel: zod_1.z.string().trim().min(1, 'Unitatea de măsură este obligatorie.'),
    unitPrice: zod_1.z
        .string()
        .trim()
        .regex(UNSIGNED_MONEY_REGEX, 'Prețul trebuie să fie un număr pozitiv cu cel mult 2 zecimale.'),
    vatCategory: zod_1.z.enum(VAT_CATEGORY_VALUES, 'Categorie TVA invalidă.'),
});
// Bare (undefaulted) field definitions, shared by create and update — same
// reasoning as firm.schema.ts's `firmFields` / series.schema.ts's
// `seriesFields`: zod's `.default()` fires on ANY `undefined` input, even a
// field simply absent from a PATCH body, even after `.partial()`. Applying
// `.default()` here and reusing this object for
// `updateInvoiceDraftSchema.partial()` would mean a PATCH `{notes: "X"}`
// alone silently resets paymentMeansCode back to '30' and currency back to
// 'RON'. Defaults are therefore only layered on in
// `createInvoiceDraftSchema` below.
//
// Two fields deliberately do NOT exist in this shape:
//  - `issueDate`, because a draft has no issue date at all — it is set by the
//    issue endpoint, at the same moment (and in the same transaction) the
//    number is allocated. Accepting it on a draft would invite the exact
//    "pre-dated invoice" pattern gapless numbering exists to prevent.
//  - `kind`, because v1 only ever creates STANDARD invoices through this
//    endpoint (ruling R21); STORNO is produced server-side by the storno
//    endpoint and PROFORMA is out of scope.
const invoiceFields = {
    companyId: zod_1.z.uuid('companyId invalid.'),
    clientId: zod_1.z.uuid('clientId invalid.'),
    seriesId: zod_1.z.uuid('seriesId este obligatoriu și trebuie să fie un UUID valid.'),
    // Ruling R19 — multi-currency invoicing (and the exchange-rate handling it
    // drags in) is out of scope for v1, so this is a literal, not a 3-letter
    // string that would silently accept EUR and then compute RON totals.
    currency: zod_1.z.literal('RON', 'Doar moneda RON este acceptată în această versiune.'),
    supplyDate: isoDateField.optional(),
    dueDate: isoDateField.optional(),
    paymentMeansCode: zod_1.z.enum(exports.PAYMENT_MEANS_CODES, 'Modalitate de plată invalidă.'),
    bankAccountId: zod_1.z.uuid('bankAccountId invalid.').optional(),
    notes: zod_1.z.string().trim().optional(),
    internalNotes: zod_1.z.string().trim().optional(),
    lines: zod_1.z.array(exports.invoiceLineInputSchema).min(1, 'Factura trebuie să aibă cel puțin o linie.'),
};
exports.createInvoiceDraftSchema = zod_1.z.object({
    ...invoiceFields,
    currency: invoiceFields.currency.default('RON'),
    paymentMeansCode: invoiceFields.paymentMeansCode.default('30'),
});
// `companyId` is omitted (same as product/client update schemas): a draft
// cannot change owner. When `lines` IS present it is a FULL replacement of
// the line set, never a merge — the service deletes and re-inserts, so
// positions stay 1..n and totals are recomputed from exactly what was sent.
exports.updateInvoiceDraftSchema = zod_1.z.object(invoiceFields).omit({ companyId: true }).partial();
exports.issueInvoiceSchema = zod_1.z.object({
    issueDate: isoDateField.optional(),
});
exports.stornoInvoiceSchema = zod_1.z.object({
    issueDate: isoDateField.optional(),
    // Defaults to the original's series; an explicit one lets a firm keep its
    // credit notes in a dedicated series.
    seriesId: zod_1.z.uuid('seriesId invalid.').optional(),
});
/** Max invoices per bulk PDF download — every PDF may need a fresh render,
 * and the ZIP is assembled in memory. */
exports.INVOICE_PDF_ARCHIVE_MAX = 200;
exports.invoicePdfArchiveSchema = zod_1.z.object({
    ids: zod_1.z
        .array(zod_1.z.uuid('id invalid.'))
        .min(1, 'Selectează cel puțin o factură.')
        .max(exports.INVOICE_PDF_ARCHIVE_MAX, `Cel mult ${exports.INVOICE_PDF_ARCHIVE_MAX} facturi odată.`),
});
exports.createPaymentSchema = zod_1.z.object({
    paidOn: isoDateField,
    // Signed: settling a storno (whose total is negative) is a negative
    // payment — the money moves the other way. Zero is meaningless and is also
    // rejected by the payment_amount_nonzero CHECK constraint.
    amount: zod_1.z
        .string()
        .trim()
        .regex(SIGNED_MONEY_REGEX, 'Suma trebuie să fie un număr cu cel mult 2 zecimale.')
        .refine((value) => !ZERO_REGEX.test(value), 'Suma nu poate fi zero.'),
    method: zod_1.z.enum(PAYMENT_METHOD_VALUES, 'Metodă de plată invalidă.'),
    reference: zod_1.z.string().trim().min(1).optional(),
    notes: zod_1.z.string().trim().optional(),
});
/**
 * The `clientIds` filter (the Facturi page's multi-select client filter, and
 * the same-scoped stat cards). Accepts one id or many: Express/qs yields a
 * bare string for a single `clientIds=…` and an array for several, and
 * different HTTP clients serialize one value differently (axios `clientIds[]=`
 * vs. a repeated `clientIds=`), so normalize to an array before validating
 * each as a UUID. Absent -> undefined (no client filter). An empty array is
 * also treated as "no filter" by the service.
 */
const clientIdsField = zod_1.z.preprocess((value) => (value === undefined ? undefined : Array.isArray(value) ? value : [value]), zod_1.z.array(zod_1.z.uuid('clientId invalid.')).optional());
exports.listInvoicesQuerySchema = common_schema_1.paginationQuerySchema.extend({
    companyId: zod_1.z.uuid('companyId este obligatoriu și trebuie să fie un UUID valid.'),
    status: zod_1.z.enum(INVOICE_STATUS_VALUES, 'Status invalid.').optional(),
    clientIds: clientIdsField,
    seriesId: zod_1.z.uuid('seriesId invalid.').optional(),
    paymentStatus: zod_1.z.enum(PAYMENT_STATUS_VALUES, 'Status de plată invalid.').optional(),
    /** "de încasat" — issued invoices not yet fully paid (payment_status <>
     * PAID). Lets the "Emise" segment be disjoint from "Încasate". */
    unpaid: zod_1.z
        .enum(['true', 'false'])
        .optional()
        .transform((value) => value === 'true'),
    /** "restante" — issued, not fully paid, past due date. */
    overdue: zod_1.z
        .enum(['true', 'false'])
        .optional()
        .transform((value) => value === 'true'),
    dateFrom: isoDateField.optional(),
    dateTo: isoDateField.optional(),
    sortBy: zod_1.z.enum(['number', 'issueDate', 'client', 'total', 'remaining']).optional(),
    sortOrder: zod_1.z.enum(['asc', 'desc']).optional(),
});
/**
 * `GET /invoices/summary` — the payment-state breakdown behind the Facturi
 * page's stat cards (facturat / încasat / neîncasat / restante). Deliberately
 * only the SCOPE filters of `listInvoicesQuerySchema`: companyId + client +
 * series + search + date range. The status/paymentStatus/unpaid/overdue
 * filters are intentionally absent — the four cards ARE the payment-state
 * breakdown, so letting the list's status chips also narrow them would make
 * the breakdown degenerate (choosing "Încasate" would zero three cards). The
 * cards therefore always summarize the full ISSUED set within the selected
 * scope, while the status chips filter only the table below them.
 */
exports.invoiceSummaryQuerySchema = zod_1.z.object({
    companyId: zod_1.z.uuid('companyId este obligatoriu și trebuie să fie un UUID valid.'),
    clientIds: clientIdsField,
    seriesId: zod_1.z.uuid('seriesId invalid.').optional(),
    search: zod_1.z.string().trim().optional(),
    dateFrom: isoDateField.optional(),
    dateTo: isoDateField.optional(),
});
